import { useId } from "react";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FieldGrid, SelectInputField, TextInputField } from "@/base";
import { AddressFieldset } from "@/components/AddressFieldset";
import { PhoneListField } from "@/components/PhoneListField";
import { OwnerIndemnityFields } from "@/components/account-record/OwnerIndemnityFields";
import { buttonClass } from "@/components/buttonStyles";
import {
  linkSpouse,
  personLabel,
  setMaritalStatus,
  type PersonRow,
} from "@/lib/accountRecordForm";
import {
  OWNER_RELATIONSHIP_LABELS,
  type OwnerChoice,
  type OwnerDraft,
} from "@/lib/accountOwners";
import { formatAddress } from "@/lib/address";
import { phoneRowErrors } from "@/lib/phone";
import { maskedSsn } from "@/lib/personnelPii";
import type { PersonnelPii } from "@/lib/brokerAccounts";

// BRK-S408 — add or edit one owner (or an owner's spouse) on the Surety
// account's Owners & principals tab.
//
// The share, marital status and spouse are the Contacts tab's own fields
// (`OwnerIndemnityFields`, BRK-S281), so the two surfaces word them the same
// and agree about who signs. The marital vocabulary is the six-value
// `MARITAL_STATUSES` — never the wireframe's Married / Not married (D8).
//
// The Relationship select offers only what PMS stores as a fact (BRK-S434):
// Owner, or Non-Owner Indemnitor (PMS-S846). A spouse is one by
// their link and a principal owner by PMS's own resolution, so neither is
// chosen here; a non-owner indemnitor has no share field.
//
// SSN is WRITE-ONLY (the pinned account PUT takes `ssn`): a password-type
// input, never pre-filled, cleared after a refusal, blank keeps PMS's value.
// Beside it the dialog shows the last four PMS holds, and nothing more, ever.

const SECTION = "flex flex-col gap-3";
const CHOICES: readonly OwnerChoice[] = ["owner", "non_owner_indemnitor"];
const HEADING = "text-sm font-semibold text-brk-text";

export function OwnerDialog({
  draft,
  onChange,
  pii,
  errors,
  busy,
  onCancel,
  onSave,
}: {
  draft: OwnerDraft;
  onChange: (next: OwnerDraft) => void;
  /** What PMS holds about this person's SSN; null for someone new. */
  pii: PersonnelPii | null;
  errors: string[];
  busy: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  const titleId = useId();
  const index = draft.people.findIndex((p) => p.key === draft.key);
  const row = draft.people[index];
  if (!row) return null;
  const title = draft.isNew
    ? "Add owner or indemnitor"
    : `Edit ${personLabel(row, index)}`;
  const indemnitor = draft.relationship === "non_owner_indemnitor";

  const setRow = (patch: Partial<PersonRow>) =>
    onChange({
      ...draft,
      people: draft.people.map((p) =>
        p.key === draft.key ? { ...p, ...patch } : p,
      ),
    });
  const newSpouse = row.spouse.kind === "new";
  const ownLegal = !(draft.sameAsOwner && draft.legalSameAsOwner);

  return (
    <Dialog
      open
      onClose={busy ? undefined : onCancel}
      fullWidth
      maxWidth="md"
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        <form
          noValidate
          id={`${titleId}-form`}
          className="flex flex-col gap-6 pt-1"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <div className={SECTION}>
            <FieldGrid min={180}>
              <TextInputField
                label="First name"
                value={row.firstName}
                onChange={(firstName) => setRow({ firstName })}
              />
              <TextInputField
                label="Last name"
                value={row.lastName}
                onChange={(lastName) => setRow({ lastName })}
              />
              <TextInputField
                label="Title"
                value={row.title}
                onChange={(title) => setRow({ title })}
              />
              {draft.relationship !== null && (
                <SelectInputField
                  label="Relationship"
                  value={draft.relationship}
                  options={CHOICES.map((value) => ({
                    value,
                    label: OWNER_RELATIONSHIP_LABELS[value],
                  }))}
                  onChange={(value) =>
                    onChange({
                      ...draft,
                      relationship: CHOICES.find((c) => c === value) ?? "owner",
                    })
                  }
                />
              )}
            </FieldGrid>
            {!indemnitor && (
              <OwnerIndemnityFields
                row={row}
                people={draft.people}
                canEdit
                onShare={(share) => setRow({ share })}
                onMaritalStatus={(status) =>
                  onChange({
                    ...draft,
                    people: setMaritalStatus(draft.people, draft.key, status),
                  })
                }
                onSpouse={(choice) =>
                  onChange({
                    ...draft,
                    people: linkSpouse(draft.people, draft.key, choice),
                  })
                }
              />
            )}
          </div>

          {newSpouse && (
            <div className={SECTION}>
              {/* PMS creates the spouse inline with these, in the same write
                  (PMS-S392); their own details stay editable from their row. */}
              <h3 className={HEADING}>Spouse’s phone and address</h3>
              <PhoneListField
                label="Spouse’s phone"
                value={draft.newSpouse.phones}
                onChange={(phones) =>
                  onChange({
                    ...draft,
                    newSpouse: { ...draft.newSpouse, phones },
                  })
                }
                errors={phoneRowErrors(draft.newSpouse.phones)}
              />
              <Toggle
                label="Same legal address as the owner"
                checked={draft.newSpouse.legalSameAsOwner}
                onChange={(legalSameAsOwner) =>
                  onChange({
                    ...draft,
                    newSpouse: { ...draft.newSpouse, legalSameAsOwner },
                  })
                }
              />
              {!draft.newSpouse.legalSameAsOwner && (
                <AddressFieldset
                  bare
                  withLine2
                  groupLabel="Spouse’s legal address"
                  value={draft.newSpouse.legal}
                  onChange={(legal) =>
                    onChange({
                      ...draft,
                      newSpouse: { ...draft.newSpouse, legal },
                    })
                  }
                />
              )}
            </div>
          )}

          <div className={SECTION}>
            <h3 className={HEADING}>Legal address</h3>
            {draft.sameAsOwner && (
              <Toggle
                label={`Same legal address as ${draft.sameAsOwner.name}`}
                checked={draft.legalSameAsOwner}
                onChange={(legalSameAsOwner) =>
                  onChange({ ...draft, legalSameAsOwner })
                }
              />
            )}
            {ownLegal ? (
              <AddressFieldset
                bare
                withLine2
                groupLabel="Legal address"
                value={draft.legal}
                onChange={(legal) => onChange({ ...draft, legal })}
              />
            ) : (
              <p className="text-sm text-brk-dim">
                {formatAddress(draft.sameAsOwner?.address)}
              </p>
            )}
            <Toggle
              label="Mailing address same as legal"
              checked={draft.mailingSame}
              onChange={(mailingSame) => onChange({ ...draft, mailingSame })}
            />
            {!draft.mailingSame && (
              <>
                <h3 className={HEADING}>Mailing address</h3>
                <AddressFieldset
                  bare
                  withLine2
                  groupLabel="Mailing address"
                  value={draft.mailing}
                  onChange={(mailing) => onChange({ ...draft, mailing })}
                />
              </>
            )}
          </div>

          <div className={SECTION}>
            <h3 className={HEADING}>Contact</h3>
            <FieldGrid min={220}>
              <TextInputField
                label="Email"
                type="email"
                inputMode="email"
                value={row.email}
                onChange={(email) => setRow({ email })}
              />
            </FieldGrid>
            <PhoneListField
              label="Phone"
              value={row.phones}
              onChange={(phones) => setRow({ phones })}
              errors={phoneRowErrors(row.phones)}
            />
          </div>

          <div className={SECTION}>
            <h3 className={HEADING}>SSN</h3>
            <FieldGrid min={220}>
              <TextInputField
                label="SSN"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                placeholder="•••-••-••••"
                value={draft.ssn}
                onChange={(ssn) => onChange({ ...draft, ssn })}
                helperText={
                  pii
                    ? `${ssnLine(pii)}. Leave blank to keep it.`
                    : "Optional. Signal keeps it hidden."
                }
              />
            </FieldGrid>
          </div>

          {errors.length > 0 && (
            <div role="alert" className="text-sm text-brk-danger">
              {errors.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={buttonClass({ variant: "secondary" })}
        >
          Cancel
        </button>
        <button
          type="submit"
          form={`${titleId}-form`}
          disabled={busy}
          className={buttonClass()}
        >
          {busy ? "Saving…" : draft.isNew ? "Add to account" : "Save changes"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

/** The SSN PMS holds, as much as a broker may ever see of it. */
function ssnLine(pii: PersonnelPii): string {
  // `pii` is required on the read; a person the dev stub just wrote may lack
  // the `ssn` block, so read it defensively.
  const ssn: PersonnelPii["ssn"] | undefined = pii.ssn;
  if (ssn?.last4) return `SSN on file: ${maskedSsn(ssn.last4)}`;
  return ssn?.onFile ? "SSN on file" : "No SSN on file";
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <FormControlLabel
      label={label}
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
    />
  );
}
